<template>
	<div class="cassie-value-slider-root">
		<div
			class="cassie-value-slider-wrapper"
			:class="wrapperClass"
		>
			<span
				class="cassie-value-slider__endpoint cassie-value-slider__endpoint--min"
				aria-hidden="true"
			>
				{{ min }}
			</span>
			<div class="cassie-value-slider">
				<v-slider
					v-bind="sliderBindObject"
					:value="internalValue"
					:min="min"
					:max="max"
					:step="step"
					:ticks="ticks"
					:tick-size="tickSize"
					:disabled="disabled"
					:thumb-label="thumbLabelMode"
					class="cassie-value-slider__slider"
					@input="onInput"
				/>
			</div>
			<span
				class="cassie-value-slider__endpoint cassie-value-slider__endpoint--max"
				aria-hidden="true"
			>
				{{ max }}
				<template v-if="maxType">
					{{ maxType }}
				</template>
			</span>
		</div>
		<div
			v-if="showCurrent"
			class="cassie-value-slider__current"
			:aria-live="ariaLiveMode"
		>
			<slot
				name="current"
				:value="internalValue"
			>
				Current: {{ internalValue }}{{ currentSuffix }}
			</slot>
		</div>
	</div>
</template>

<script>
// Reusable slider with min/max scale and accessible thumb label styling
export default {
	name: 'value-slider',
	inheritAttrs: false,
	props: {
		value: { type: [Number, String], default: 0 },
		min: { type: Number, default: 0 },
		max: { type: Number, default: 100 },
		// Optional descriptor to display after the max value (e.g. 'secs', 'KB', '%')
		maxType: { type: String, default: '' },
		step: { type: Number, default: 1 },
		ticks: { type: Boolean, default: false },
		tickSize: { type: [Number, String], default: 2 },
		disabled: { type: Boolean, default: false },
		showCurrent: { type: Boolean, default: false },
		currentSuffix: { type: String, default: '' },
		thumbLabel: { type: [Boolean, String], default: 'always' },
		ariaLabel: { type: String, default: '' },
		ariaLiveMode: { type: String, default: 'polite' },
		wrapperClass: { type: [String, Array, Object], default: '' }
	},
	computed: {
		internalValue () { return typeof this.value === 'string' ? Number(this.value) : this.value },
		thumbLabelMode () { return this.thumbLabel },
		sliderBindObject () {
			return {
				'aria-label': this.ariaLabel || undefined,
				'aria-valuemin': this.min,
				'aria-valuemax': this.max,
				'aria-valuenow': this.internalValue,
				...this.$attrs
			}
		}
	},
	methods: {
		onInput (val) {
			this.$emit('input', val)
			this.$emit('change', val)
		}
	}
}
</script>

<style lang="scss" scoped>
.cassie-value-slider-root { display: flex; flex-direction: column; --endpoint-offset: -4px; }
.cassie-value-slider-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
}
.cassie-value-slider__endpoint {
	font-size: 11px;
	font-weight: 600;
	color: rgba(0,0,0,0.65);
	min-width: 22px;
	text-align: center;
	user-select: none;
	line-height: 1;
	position: relative;
  margin-top: -12px;
	transform: translateY(var(--endpoint-offset)); // adjustable vertical alignment
}
.cassie-value-slider { flex: 1 1 auto; position: relative; }
.cassie-value-slider__slider { position: relative; padding-bottom: 0; margin: 0; }
/* Deep selectors so scoped styles reach Vuetify internal structure (Vuetify 2 & 3) */
.cassie-value-slider__slider ::v-deep .v-slider__thumb-label,
.cassie-value-slider__slider ::v-deep .v-slider-thumb__label {
	top: 50% !important;
	left: 50%;
	bottom: auto !important;
	transform: translate(-50%, -50%) !important;
	width: 26px !important;
	height: 26px !important;
	border-radius: 50%;
	background: var(--v-theme-primary, var(--global-primary)) !important;
	color: #fff !important;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	line-height: 1;
	box-shadow: 0 2px 4px rgba(0,0,0,0.25);
	border: 2px solid #fff;
}
.cassie-value-slider__slider ::v-deep .v-slider__thumb-label > *,
.cassie-value-slider__slider ::v-deep .v-slider-thumb__label > * { transform: none !important; }
.cassie-value-slider__slider ::v-deep .v-slider__thumb-label:before,
.cassie-value-slider__slider ::v-deep .v-slider__thumb-label:after,
.cassie-value-slider__slider ::v-deep .v-slider-thumb__label:before,
.cassie-value-slider__slider ::v-deep .v-slider-thumb__label:after { display: none !important; }
/* Center thumb container content when label overlaps */
.cassie-value-slider__slider ::v-deep .v-slider__thumb,
.cassie-value-slider__slider ::v-deep .v-slider-thumb {
	width: 0; /* prevents extra horizontal shift */
}
/* Optional: Increase track click target slightly */
.cassie-value-slider__slider ::v-deep .v-slider__track-container { margin-top: 0; }
.cassie-value-slider__current { margin-top: 4px; font-size: 12px; color: rgba(0,0,0,0.75); }
</style>
